/**
 * Editing operations — the contract every client follows.
 *
 * `applyOp(doc, op) -> { doc', inverse }` is pure and deterministic: no clocks,
 * no random ids, no I/O. Undo is "apply the inverse"; sync (Phase 2) is an
 * append-only log of these ops with server sequence numbers.
 *
 * Phase 1: page/scale/sheet ops. Phase 2: markups, measurements, layers and
 * the stamp library. Both clients (this file and Swift `CenterlineCore`) are
 * held to `fixtures/ops/vectors.json` + `fixtures/ops/markup-vectors.json`.
 *
 * Inverses are exact: a move's inverse restores the stored coordinates rather
 * than subtracting the offset back (x + dx - dx is not always x in floating
 * point), so undo always returns the very same document.
 */
import type { Box, CenterlineDocument, Id, PageInfo, PageRotation, PageScale, SheetLabel } from "./types.js";
import type { Markup, MarkupPatch, Measurement, MeasurementPatch, PayItem, StampDefinition, Vec2 } from "./markup/types.js";
import { MARKUP_KINDS, MARKUP_PATCH_FIELDS, MEASUREMENT_OPTIONAL_FIELDS, MEASUREMENT_PATCH_FIELDS } from "./markup/types.js";

export interface NewPage { pageId: Id; mediaBox: Box; cropBox?: Box; rotate?: PageRotation }

export type Operation =
  /** Insert a page record (the PDF blob is swapped separately). Default: append. */
  | { type: "addSheet"; page: NewPage; atIndex?: number; scale?: PageScale | null; sheetLabel?: SheetLabel | null }
  /** Remove a page record and everything keyed to it. */
  | { type: "removeSheet"; pageId: Id }
  /** Set or clear (null) one page's scale. */
  | { type: "setPageScale"; pageId: Id; scale: PageScale | null }
  /** Copy one page's scale to others (`applyScale(from:to:)`); the source is skipped. */
  | { type: "copyScaleToPages"; fromPageId: Id; toPageIds: Id[] }
  /** Set or clear (null) one page's sheet label. */
  | { type: "setSheetLabel"; pageId: Id; label: SheetLabel | null }
  | { type: "renameDocument"; name: string }
  /** Insert a markup (default: on top). */
  | { type: "addMarkup"; markup: Markup; atIndex?: number }
  | { type: "deleteMarkup"; id: Id }
  /** Replace whole fields of one markup (last writer wins per markup). */
  | { type: "patchMarkup"; id: Id; set: MarkupPatch }
  /** Translate a markup (box and vertices) by dx, dy page points. */
  | { type: "moveMarkup"; id: Id; dx: number; dy: number }
  | { type: "addMeasurement"; measurement: Measurement; atIndex?: number }
  | { type: "deleteMeasurement"; id: Id }
  | { type: "patchMeasurement"; id: Id; set: MeasurementPatch }
  /** Translate a takeoff (points and cutouts; the label keeps its offset). */
  | { type: "moveMeasurement"; id: Id; dx: number; dy: number }
  | { type: "setLayerVisibility"; layer: string; visible: boolean }
  /** Add a user layer (shown). Web addition; inverse is removeLayer. */
  | { type: "addLayer"; layer: string }
  /** Remove a layer nothing is on. */
  | { type: "removeLayer"; layer: string }
  /** Add or replace a saved custom stamp design. */
  | { type: "upsertStampDefinition"; stamp: StampDefinition; atIndex?: number }
  | { type: "deleteStampDefinition"; id: Id }
  /** Several ops as one undo step. Applied in order; inverse is reversed. */
  | { type: "batch"; ops: Operation[] };

export type OperationType = Operation["type"];

/** Envelope for the Phase 2 op log. Declared now so clients agree on the shape. */
export interface OpEnvelope {
  opId: Id;
  documentId: Id;
  /** Server sequence this op was authored against. */
  baseSeq: number;
  /** Assigned by the server on accept. */
  seq?: number;
  actor: string;
  /** ISO 8601, client clock (informational). */
  at: string;
  op: Operation;
}

export type OpErrorCode =
  | "unknown-page" | "duplicate-page" | "invalid-scale" | "invalid-label" | "invalid-index" | "invalid-name" | "unknown-op"
  | "unknown-markup" | "duplicate-markup" | "invalid-markup"
  | "unknown-measurement" | "duplicate-measurement" | "invalid-measurement"
  | "unknown-layer" | "unknown-stamp" | "invalid-stamp";

export class OpError extends Error {
  constructor(public readonly code: OpErrorCode, message: string) {
    super(message);
    this.name = "OpError";
  }
}

export interface ApplyResult { doc: CenterlineDocument; inverse: Operation }

function pageIndexOf(doc: CenterlineDocument, pageId: Id): number {
  const i = doc.plan.pages.findIndex((p) => p.pageId === pageId);
  if (i < 0) throw new OpError("unknown-page", `No page ${pageId}`);
  return i;
}

function reindex(pages: PageInfo[]): PageInfo[] {
  return pages.map((p, index) => (p.index === index ? p : { ...p, index }));
}

function validScale(scale: PageScale): boolean {
  return Number.isFinite(scale.feetPerPoint) && scale.feetPerPoint > 0 && typeof scale.displayLabel === "string";
}

function withEntry<T>(map: Record<Id, T>, key: Id, value: T | null | undefined): Record<Id, T> {
  const next = { ...map };
  if (value == null) delete next[key];
  else next[key] = value;
  return next;
}

export function applyOp(doc: CenterlineDocument, op: Operation): ApplyResult {
  switch (op.type) {
    case "addSheet": {
      if (doc.plan.pages.some((p) => p.pageId === op.page.pageId)) throw new OpError("duplicate-page", `Page ${op.page.pageId} already exists`);
      const at = op.atIndex ?? doc.plan.pages.length;
      if (!Number.isInteger(at) || at < 0 || at > doc.plan.pages.length) throw new OpError("invalid-index", `Index ${at} out of range`);
      if (op.scale && !validScale(op.scale)) throw new OpError("invalid-scale", "feetPerPoint must be > 0");
      const page: PageInfo = { pageId: op.page.pageId, index: at, mediaBox: op.page.mediaBox, cropBox: op.page.cropBox ?? op.page.mediaBox, rotate: op.page.rotate ?? 0 };
      const pages = [...doc.plan.pages];
      pages.splice(at, 0, page);
      const next: CenterlineDocument = {
        ...doc,
        plan: { ...doc.plan, pages: reindex(pages), pageCount: pages.length },
        pageScales: withEntry(doc.pageScales, page.pageId, op.scale ?? null),
        sheetLabels: withEntry(doc.sheetLabels, page.pageId, op.sheetLabel ?? null),
      };
      return { doc: next, inverse: { type: "removeSheet", pageId: page.pageId } };
    }
    case "removeSheet": {
      const i = pageIndexOf(doc, op.pageId);
      const page = doc.plan.pages[i]!;
      const pages = doc.plan.pages.filter((_, j) => j !== i);
      const next: CenterlineDocument = {
        ...doc,
        plan: { ...doc.plan, pages: reindex(pages), pageCount: pages.length },
        pageScales: withEntry(doc.pageScales, op.pageId, null),
        sheetLabels: withEntry(doc.sheetLabels, op.pageId, null),
      };
      const addBack: Operation = {
        type: "addSheet",
        page: { pageId: page.pageId, mediaBox: page.mediaBox, cropBox: page.cropBox, rotate: page.rotate },
        atIndex: i,
        scale: doc.pageScales[op.pageId] ?? null,
        sheetLabel: doc.sheetLabels[op.pageId] ?? null,
      };
      // Everything drawn on the sheet goes with it, and comes back on undo at
      // its old stacking position (ascending inserts rebuild the order exactly).
      const restore: Operation[] = [];
      doc.measurements.forEach((m, at) => { if (m.pageId === op.pageId) restore.push({ type: "addMeasurement", measurement: m, atIndex: at }); });
      doc.markups.forEach((m, at) => { if (m.pageId === op.pageId) restore.push({ type: "addMarkup", markup: m, atIndex: at }); });
      if (restore.length) {
        next.measurements = doc.measurements.filter((m) => m.pageId !== op.pageId);
        next.markups = doc.markups.filter((m) => m.pageId !== op.pageId);
        return { doc: next, inverse: { type: "batch", ops: [addBack, ...restore] } };
      }
      return { doc: next, inverse: addBack };
    }
    case "setPageScale": {
      pageIndexOf(doc, op.pageId);
      if (op.scale && !validScale(op.scale)) throw new OpError("invalid-scale", "feetPerPoint must be > 0");
      const previous = doc.pageScales[op.pageId] ?? null;
      return {
        doc: { ...doc, pageScales: withEntry(doc.pageScales, op.pageId, op.scale) },
        inverse: { type: "setPageScale", pageId: op.pageId, scale: previous },
      };
    }
    case "copyScaleToPages": {
      pageIndexOf(doc, op.fromPageId);
      const source = doc.pageScales[op.fromPageId];
      if (!source) throw new OpError("invalid-scale", `Page ${op.fromPageId} has no scale to copy`);
      const targets = [...new Set(op.toPageIds)].filter((id) => id !== op.fromPageId);
      targets.forEach((id) => pageIndexOf(doc, id));
      let scales = doc.pageScales;
      const undo: Operation[] = [];
      for (const id of targets) {
        undo.push({ type: "setPageScale", pageId: id, scale: doc.pageScales[id] ?? null });
        scales = withEntry(scales, id, { ...source });
      }
      return { doc: { ...doc, pageScales: scales }, inverse: { type: "batch", ops: undo } };
    }
    case "setSheetLabel": {
      pageIndexOf(doc, op.pageId);
      if (op.label && op.label.number.trim().length === 0) throw new OpError("invalid-label", "Sheet number is empty; pass null to clear");
      const previous = doc.sheetLabels[op.pageId] ?? null;
      return {
        doc: { ...doc, sheetLabels: withEntry(doc.sheetLabels, op.pageId, op.label) },
        inverse: { type: "setSheetLabel", pageId: op.pageId, label: previous },
      };
    }
    case "renameDocument": {
      const name = op.name.trim();
      if (!name) throw new OpError("invalid-name", "Name is empty");
      return { doc: { ...doc, name }, inverse: { type: "renameDocument", name: doc.name } };
    }
    case "addMarkup": {
      checkMarkup(doc, op.markup);
      if (doc.markups.some((m) => m.id === op.markup.id)) throw new OpError("duplicate-markup", `Markup ${op.markup.id} already exists`);
      const markups = insertAt(doc.markups, op.markup, op.atIndex);
      return { doc: { ...doc, markups }, inverse: { type: "deleteMarkup", id: op.markup.id } };
    }
    case "deleteMarkup": {
      const i = markupIndex(doc, op.id);
      return { doc: { ...doc, markups: doc.markups.filter((_, j) => j !== i) }, inverse: { type: "addMarkup", markup: doc.markups[i]!, atIndex: i } };
    }
    case "patchMarkup": {
      const i = markupIndex(doc, op.id);
      const old = doc.markups[i]!;
      const { next, previous } = patchMarkup(doc, old, op.set);
      const markups = [...doc.markups];
      markups[i] = next;
      return { doc: { ...doc, markups }, inverse: { type: "patchMarkup", id: op.id, set: previous } };
    }
    case "moveMarkup": {
      const i = markupIndex(doc, op.id);
      if (!isFiniteNum(op.dx) || !isFiniteNum(op.dy)) throw new OpError("invalid-markup", "Move offset must be finite");
      const old = doc.markups[i]!;
      const moved = { ...old, bounds: [old.bounds[0] + op.dx, old.bounds[1] + op.dy, old.bounds[2], old.bounds[3]] as Box } as Markup;
      const previous: MarkupPatch = { bounds: old.bounds };
      if (old.vertices) { moved.vertices = translate(old.vertices, op.dx, op.dy); previous.vertices = old.vertices; }
      const markups = [...doc.markups];
      markups[i] = moved;
      return { doc: { ...doc, markups }, inverse: { type: "patchMarkup", id: op.id, set: previous } };
    }
    case "addMeasurement": {
      checkMeasurement(doc, op.measurement);
      if (doc.measurements.some((m) => m.id === op.measurement.id)) throw new OpError("duplicate-measurement", `Takeoff ${op.measurement.id} already exists`);
      return { doc: { ...doc, measurements: insertAt(doc.measurements, op.measurement, op.atIndex) }, inverse: { type: "deleteMeasurement", id: op.measurement.id } };
    }
    case "deleteMeasurement": {
      const i = measurementIndex(doc, op.id);
      return { doc: { ...doc, measurements: doc.measurements.filter((_, j) => j !== i) }, inverse: { type: "addMeasurement", measurement: doc.measurements[i]!, atIndex: i } };
    }
    case "patchMeasurement": {
      const i = measurementIndex(doc, op.id);
      const old = doc.measurements[i]!;
      const { next, previous } = patchMeasurement(doc, old, op.set);
      const measurements = [...doc.measurements];
      measurements[i] = next;
      return { doc: { ...doc, measurements }, inverse: { type: "patchMeasurement", id: op.id, set: previous } };
    }
    case "moveMeasurement": {
      const i = measurementIndex(doc, op.id);
      if (!isFiniteNum(op.dx) || !isFiniteNum(op.dy)) throw new OpError("invalid-measurement", "Move offset must be finite");
      const old = doc.measurements[i]!;
      const moved: Measurement = { ...old, points: translate(old.points, op.dx, op.dy) };
      const previous: MeasurementPatch = { points: old.points };
      if (old.cutouts) { moved.cutouts = old.cutouts.map((ring) => translate(ring, op.dx, op.dy)); previous.cutouts = old.cutouts; }
      const measurements = [...doc.measurements];
      measurements[i] = moved;
      return { doc: { ...doc, measurements }, inverse: { type: "patchMeasurement", id: op.id, set: previous } };
    }
    case "setLayerVisibility": {
      if (!doc.layers.available.includes(op.layer)) throw new OpError("unknown-layer", `No layer ${op.layer}`);
      const was = doc.layers.visible.includes(op.layer);
      const shown = new Set(doc.layers.visible);
      if (op.visible) shown.add(op.layer); else shown.delete(op.layer);
      const visible = [...doc.layers.available.filter((l) => shown.has(l)), ...doc.layers.visible.filter((l) => !doc.layers.available.includes(l))];
      return { doc: { ...doc, layers: { ...doc.layers, visible } }, inverse: { type: "setLayerVisibility", layer: op.layer, visible: was } };
    }
    case "addLayer": {
      const name = typeof op.layer === "string" ? op.layer.trim().slice(0, 60) : "";
      if (!name) throw new OpError("unknown-layer", "Layer name is required");
      if (doc.layers.available.includes(name)) return { doc, inverse: { type: "batch", ops: [] } };
      return { doc: { ...doc, layers: { ...doc.layers, available: [...doc.layers.available, name], visible: [...doc.layers.visible, name] } }, inverse: { type: "removeLayer", layer: name } };
    }
    case "removeLayer": {
      if (!doc.layers.available.includes(op.layer)) return { doc, inverse: { type: "batch", ops: [] } };
      if (doc.markups.some((m) => m.layer === op.layer) || doc.measurements.some((m) => m.layer === op.layer)) throw new OpError("unknown-layer", `Layer ${op.layer} still has items`);
      const available = doc.layers.available.filter((l) => l !== op.layer);
      return { doc: { ...doc, layers: { available, visible: doc.layers.visible.filter((l) => l !== op.layer), current: doc.layers.current === op.layer ? available[0] ?? "Existing" : doc.layers.current } }, inverse: { type: "addLayer", layer: op.layer } };
    }
    case "upsertStampDefinition": {
      checkStamp(op.stamp);
      const i = doc.stampLibrary.findIndex((s) => s.id === op.stamp.id);
      if (i >= 0) {
        const stampLibrary = [...doc.stampLibrary];
        stampLibrary[i] = op.stamp;
        return { doc: { ...doc, stampLibrary }, inverse: { type: "upsertStampDefinition", stamp: doc.stampLibrary[i]! } };
      }
      return { doc: { ...doc, stampLibrary: insertAt(doc.stampLibrary, op.stamp, op.atIndex) }, inverse: { type: "deleteStampDefinition", id: op.stamp.id } };
    }
    case "deleteStampDefinition": {
      const i = doc.stampLibrary.findIndex((s) => s.id === op.id);
      if (i < 0) throw new OpError("unknown-stamp", `No stamp design ${op.id}`);
      return { doc: { ...doc, stampLibrary: doc.stampLibrary.filter((_, j) => j !== i) }, inverse: { type: "upsertStampDefinition", stamp: doc.stampLibrary[i]!, atIndex: i } };
    }
    case "batch": {
      let current = doc;
      const inverses: Operation[] = [];
      for (const inner of op.ops) {
        const r = applyOp(current, inner);
        current = r.doc;
        inverses.unshift(r.inverse);
      }
      return { doc: current, inverse: { type: "batch", ops: inverses } };
    }
    default: {
      const unknown = op as { type?: unknown };
      throw new OpError("unknown-op", `Unknown operation ${String(unknown.type)}`);
    }
  }
}

// MARK: - Markup and measurement helpers

const isFiniteNum = (x: unknown): x is number => typeof x === "number" && Number.isFinite(x);
const isVec2 = (p: unknown): p is Vec2 => Array.isArray(p) && p.length === 2 && isFiniteNum(p[0]) && isFiniteNum(p[1]);
const isVec2Array = (v: unknown): v is Vec2[] => Array.isArray(v) && v.every(isVec2);
const isBox = (b: unknown): b is Box => Array.isArray(b) && b.length === 4 && b.every(isFiniteNum) && (b[2] as number) >= 0 && (b[3] as number) >= 0;
const isObject = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);

function translate(points: readonly Vec2[], dx: number, dy: number): Vec2[] {
  return points.map(([x, y]) => [x + dx, y + dy]);
}

function insertAt<T>(list: readonly T[], item: T, atIndex: number | undefined): T[] {
  const at = atIndex ?? list.length;
  if (!Number.isInteger(at) || at < 0 || at > list.length) throw new OpError("invalid-index", `Index ${at} out of range`);
  const next = [...list];
  next.splice(at, 0, item);
  return next;
}

function markupIndex(doc: CenterlineDocument, id: Id): number {
  const i = doc.markups.findIndex((m) => m.id === id);
  if (i < 0) throw new OpError("unknown-markup", `No markup ${id}`);
  return i;
}
function measurementIndex(doc: CenterlineDocument, id: Id): number {
  const i = doc.measurements.findIndex((m) => m.id === id);
  if (i < 0) throw new OpError("unknown-measurement", `No takeoff ${id}`);
  return i;
}
function hasPage(doc: CenterlineDocument, pageId: unknown): boolean {
  return typeof pageId === "string" && doc.plan.pages.some((p) => p.pageId === pageId);
}
/** `mot` carries the symbol name; every other kind carries an object. */
function styleFits(kind: string, style: unknown): boolean {
  return kind === "mot" ? typeof style === "string" && style.length > 0 : isObject(style);
}

function checkMarkup(doc: CenterlineDocument, m: Markup): void {
  const bad = (why: string) => new OpError("invalid-markup", `Markup ${String((m as { id?: unknown })?.id)}: ${why}`);
  if (!isObject(m) || typeof m.id !== "string" || !m.id) throw bad("missing id");
  if (!MARKUP_KINDS.includes(m.kind)) throw bad(`unknown kind ${String(m.kind)}`);
  if (!hasPage(doc, m.pageId)) throw new OpError("unknown-page", `No page ${String(m.pageId)}`);
  if (!isBox(m.bounds)) throw bad("bounds must be [x, y, w, h] with w, h ≥ 0");
  if (!isFiniteNum(m.rotationDegrees)) throw bad("rotation must be a number");
  if (typeof m.layer !== "string" || !m.layer) throw bad("layer is empty");
  if (typeof m.createdAt !== "string") throw bad("createdAt must be an ISO string");
  if (m.vertices !== undefined && !isVec2Array(m.vertices)) throw bad("vertices must be [[x, y], …]");
  if (!styleFits(m.kind, m.style)) throw bad(`style does not fit kind ${m.kind}`);
}

function patchMarkup(doc: CenterlineDocument, old: Markup, set: MarkupPatch): { next: Markup; previous: MarkupPatch } {
  if (!isObject(set)) throw new OpError("invalid-markup", "Patch must be an object");
  const next = { ...old } as Record<string, unknown>;
  const previous: Record<string, unknown> = {};
  for (const key of Object.keys(set)) {
    if (!(MARKUP_PATCH_FIELDS as readonly string[]).includes(key)) throw new OpError("invalid-markup", `Field ${key} cannot be patched`);
    const value = (set as Record<string, unknown>)[key];
    const before = (old as unknown as Record<string, unknown>)[key];
    switch (key) {
      case "bounds": if (!isBox(value)) throw new OpError("invalid-markup", "bounds must be [x, y, w, h] with w, h ≥ 0"); break;
      case "rotationDegrees": if (!isFiniteNum(value)) throw new OpError("invalid-markup", "rotation must be a number"); break;
      case "layer": if (typeof value !== "string" || !value) throw new OpError("invalid-markup", "layer is empty"); break;
      case "pageId": if (!hasPage(doc, value)) throw new OpError("unknown-page", `No page ${String(value)}`); break;
      case "vertices": if (value !== null && !isVec2Array(value)) throw new OpError("invalid-markup", "vertices must be [[x, y], …] or null"); break;
      case "style": if (!styleFits(old.kind, value)) throw new OpError("invalid-markup", `style does not fit kind ${old.kind}`); break;
    }
    previous[key] = before === undefined ? null : before;
    if (value === null) delete next[key]; else next[key] = value;
  }
  return { next: next as unknown as Markup, previous: previous as MarkupPatch };
}

const MEASUREMENT_KINDS = ["linear", "arrow", "area", "count"];

function checkMeasurementField(doc: CenterlineDocument, key: string, value: unknown): void {
  const bad = (why: string) => new OpError("invalid-measurement", `${key}: ${why}`);
  switch (key) {
    case "pageId": if (!hasPage(doc, value)) throw new OpError("unknown-page", `No page ${String(value)}`); return;
    case "points": if (!isVec2Array(value)) throw bad("must be [[x, y], …]"); return;
    case "label": case "manualUnit": case "sourceTag": case "notes": if (typeof value !== "string") throw bad("must be text"); return;
    case "layer": if (typeof value !== "string" || !value) throw bad("is empty"); return;
    case "areaUnit": if (!["SF", "SY", "AC"].includes(value as string)) throw bad("must be SF, SY or AC"); return;
    case "linearUnit": if (!["LF", "FT", "M", "GM"].includes(value as string)) throw bad("must be LF, FT, M or GM"); return;
    case "manualQuantity": if (!isFiniteNum(value)) throw bad("must be a number"); return;
    case "cutouts": if (!Array.isArray(value) || !value.every(isVec2Array)) throw bad("must be rings of [x, y]"); return;
    case "payItem": case "labelStyle": case "depth": if (!isObject(value)) throw bad("must be an object"); return;
    case "color": if (typeof value !== "string") throw bad("must be a palette colour"); return;
  }
}

function checkMeasurement(doc: CenterlineDocument, m: Measurement): void {
  if (!isObject(m) || typeof m.id !== "string" || !m.id) throw new OpError("invalid-measurement", "Takeoff is missing its id");
  if (!MEASUREMENT_KINDS.includes(m.kind)) throw new OpError("invalid-measurement", `Unknown takeoff kind ${String(m.kind)}`);
  if (typeof m.createdAt !== "string") throw new OpError("invalid-measurement", "createdAt must be an ISO string");
  for (const key of ["pageId", "points", "label", "layer", "areaUnit", "linearUnit"]) checkMeasurementField(doc, key, (m as unknown as Record<string, unknown>)[key]);
  for (const key of MEASUREMENT_OPTIONAL_FIELDS) {
    const v = (m as unknown as Record<string, unknown>)[key];
    if (v !== undefined) checkMeasurementField(doc, key, v);
  }
}

function patchMeasurement(doc: CenterlineDocument, old: Measurement, set: MeasurementPatch): { next: Measurement; previous: MeasurementPatch } {
  if (!isObject(set)) throw new OpError("invalid-measurement", "Patch must be an object");
  const next = { ...old } as Record<string, unknown>;
  const previous: Record<string, unknown> = {};
  for (const key of Object.keys(set)) {
    if (!(MEASUREMENT_PATCH_FIELDS as readonly string[]).includes(key)) throw new OpError("invalid-measurement", `Field ${key} cannot be patched`);
    const value = (set as Record<string, unknown>)[key];
    const optional = (MEASUREMENT_OPTIONAL_FIELDS as readonly string[]).includes(key);
    if (value === null) { if (!optional) throw new OpError("invalid-measurement", `${key} cannot be cleared`); }
    else checkMeasurementField(doc, key, value);
    const before = (old as unknown as Record<string, unknown>)[key];
    previous[key] = before === undefined ? null : before;
    if (value === null) delete next[key]; else next[key] = value;
  }
  return { next: next as unknown as Measurement, previous: previous as MeasurementPatch };
}

function checkStamp(s: StampDefinition): void {
  if (!isObject(s) || typeof s.id !== "string" || !s.id) throw new OpError("invalid-stamp", "Stamp design is missing its id");
  if (typeof s.name !== "string" || !s.name.trim()) throw new OpError("invalid-stamp", "Stamp design needs a name");
  if (!isObject(s.details) || typeof s.details.title !== "string") throw new OpError("invalid-stamp", "Stamp design needs details with a title");
  if (!Array.isArray(s.size) || s.size.length !== 2 || !s.size.every((v) => isFiniteNum(v) && v > 0)) throw new OpError("invalid-stamp", "Stamp size must be [width, height] > 0");
}

/** What an op touches, for the server's stale-write rules. */
export interface OpTargets { markups: Id[]; measurements: Id[]; pages: Id[]; pageTree: boolean; stamps: Id[]; layers: boolean; document: boolean }

export function opTargets(op: Operation): OpTargets {
  const t: OpTargets = { markups: [], measurements: [], pages: [], pageTree: false, stamps: [], layers: false, document: false };
  const visit = (o: Operation) => {
    switch (o.type) {
      case "addSheet": t.pageTree = true; t.pages.push(o.page.pageId); break;
      case "removeSheet": t.pageTree = true; t.pages.push(o.pageId); break;
      case "setPageScale": case "setSheetLabel": t.pages.push(o.pageId); break;
      case "copyScaleToPages": t.pages.push(...o.toPageIds); break;
      case "renameDocument": t.document = true; break;
      case "addMarkup": t.markups.push(o.markup.id); break;
      case "deleteMarkup": case "patchMarkup": case "moveMarkup": t.markups.push(o.id); break;
      case "addMeasurement": t.measurements.push(o.measurement.id); break;
      case "deleteMeasurement": case "patchMeasurement": case "moveMeasurement": t.measurements.push(o.id); break;
      case "setLayerVisibility": case "addLayer": case "removeLayer": t.layers = true; break;
      case "upsertStampDefinition": t.stamps.push(o.stamp.id); break;
      case "deleteStampDefinition": t.stamps.push(o.id); break;
      case "batch": o.ops.forEach(visit); break;
    }
  };
  visit(op);
  return t;
}

export function applyOps(doc: CenterlineDocument, ops: readonly Operation[]): CenterlineDocument {
  return ops.reduce((d, op) => applyOp(d, op).doc, doc);
}

/** `setSheetLabel(_:title:for:)` input rules: trim; empty → clear; typed → manual. */
export function sheetLabelFromInput(number: string, title?: string): SheetLabel | null {
  const trimmed = number.trim();
  if (!trimmed) return null;
  return title ? { number: trimmed, title, isManual: true } : { number: trimmed, isManual: true };
}

/** The takeoff fields a rename can change. Sent as a `patchMeasurement` op, so undo/redo, offline queueing and sync all apply. */
export interface MeasurementUpdate { label?: string; payItem?: PayItem | null; notes?: string | null }

const samePayItem = (a?: PayItem | null, b?: PayItem | null) => (!a && !b) || (!!a && !!b && a.number === b.number && a.name === b.name && a.unit === b.unit);

/** Only the fields that actually change (so a rename doesn't stomp a teammate's concurrent edit of another field). Blank labels keep the old one. */
export function measurementChanges(m: Measurement, next: MeasurementUpdate): MeasurementUpdate {
  const set: MeasurementUpdate = {};
  if (next.label !== undefined) { const l = next.label.trim(); if (l && l !== m.label) set.label = l; }
  if (next.payItem !== undefined && !samePayItem(m.payItem, next.payItem)) set.payItem = next.payItem;
  if (next.notes !== undefined && (next.notes || null) !== (m.notes || null)) set.notes = next.notes || null;
  return set;
}

/** How the takeoff totals group alike items: by pay item number when there is one, else by label (case-insensitive), always per unit. */
export function takeoffGroupKey(m: Pick<Measurement, "label" | "payItem">, unit: string): string {
  return m.payItem?.number ? `pay:${m.payItem.number}|${unit}` : `label:${m.label.trim().toLowerCase()}|${unit}`;
}
